import { BoltIcon, InformationCircleIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@/components/common/helpers/ReactHooks";
import Tooltip from "@/components/common/tooltip/Tooltip";
import { getNumberWithOrdinal, toTitleCase } from "@/components/common/validation/Validators";
import { addBusinessDays, addDays, endOfMonth, format as formatDate, set, subDays } from "date-fns";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import ReactSelect from "react-select";
import { Actions as ADVISOR_ACTIONS } from "../../../store/actions/advisorActions";
import EverRollModal from "./EverRollModal";

const format = (date, format) => {
	try {
		return formatDate(date, format);
	} catch {
		return ""
	}
}

const EverRoll = ({ organisations, getEverRollSettings, orgPaySchedules, openEverRollModal, everRollRules }) => {
	const [company, setCompany] = useQuery("company");

	const filteredOrganisations = useMemo(() => {
		// filter by company amd status if any are set
		return orgPaySchedules?.filter((p) => {
			const companyMatch = company ? p.org == company : true;
			return companyMatch;
		});
	}, [company, orgPaySchedules?.length]);

	useEffect(() => {
		getEverRollSettings();
	}, []);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	return (
		<>
			<EverRollModal />
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">EverRoll™</h1>
						</div>
					</div>
				</div>
			</div>
			<div className="content">
				<div className="block-header pr-0 pl-0">
					<div
						className="pull-right"
						style={{ width: 250, marginRight: "1rem" }}
					>
						<div className="form-material">
							<ReactSelect
								isClearable
								options={organisations}
								styles={{
									menu: (provided) => ({ ...provided, zIndex: 10 }),
								}}
								value={organisations?.find((o) => o.value == company)}
								onChange={(selected) => {
									setCompany(selected?.value || null);
								}}
							/>
							<label>Organisation</label>
						</div>
					</div>
				</div>
				{filteredOrganisations?.map((org) => {
					return (
						<Company
							org={org}
							everRollRules={everRollRules?.[org?.org] || []}
							openEverRollModal={openEverRollModal}
						/>
					);
				})}
			</div>
		</>
	);
};

function Company({ org, everRollRules, openEverRollModal }) {
	const { name, paySchedules, jurisdiction } = org || {};
	return (
		<div
			className="row"
			style={{ paddingBottom: "1rem", paddingTop: "1rem" }}
		>
			<h3>{name}</h3>
			<div className="form-section">
				{paySchedules?.map((schedule) => {
					return (
						<PaySchedule
							org={org.org}
							jurisdiction={jurisdiction}
							openEverRollModal={openEverRollModal}
							schedule={schedule}
							everRollRule={everRollRules?.find?.((rule) => rule.entity == schedule.id)}
						/>
					);
				})}
			</div>
		</div>
	);
}

function PaySchedule({ org, jurisdiction, schedule, openEverRollModal, everRollRule }) {
	const refDate = set(new Date(), jurisdiction === "MT" ? { date: 1, month: 0 } : { date: 1, month: 3 });

	const { name, payFrequency, payDay, type, deadline } = schedule || {};

	const first3Rules = Object.entries(everRollRule?.actions || {})?.slice(0, 3);

	// if (first3Rules) console.debug(first3Rules);

	const payType = useMemo(() => {
		if (type !== "4-4-5") {
			return {
				12: "monthly",
				13: "4 weekly",
				26: "2 weekly",
				52: "weekly",
			}[payFrequency];
		} else {
			return "4-4-5";
		}
	}, [type, payFrequency]);

	const periodStart = refDate;
	const periodEnd = useMemo(() => {
		let periodEnd = refDate;
		switch (String(payFrequency)) {
			case "52":
				periodEnd = set(periodEnd, { date: periodEnd.getDate() + 7 });
				break;
			case "26":
				periodEnd = set(periodEnd, { date: periodEnd.getDate() + 14 });
				break;
			case "13":
				periodEnd = set(periodEnd, { date: periodEnd.getDate() + 28 });
				break;
			case "12":
			default:
				if (type === "4-4-5") {
					periodEnd = set(periodEnd, { date: periodEnd.getDate() + 28 });
				} else {
					periodEnd = addDays(endOfMonth(periodStart), jurisdiction === "MT" ? 0 : 0);
				}
				break;
		}
		return periodEnd;
	}, [payFrequency, type]);

	const [paid, payDate] = useMemo(() => {
		const payDayDescription = {
			"of the period": "of the period",
			"of the month": "day of the month",
			"days": "day/s after end of period",
			"workingDays": "working day/s after end of period",
		};

		const formatter = payDay?.type === "of the month" ? getNumberWithOrdinal : (value) => value;

		const prefix = ["of the period", "of the month"].includes(payDay?.type) ? "on the " : "";

		let paid = `${prefix}${formatter(payDay?.value)} ${payDayDescription[payDay?.type]}`;

		let payDate;

		switch (payDay?.type) {
			case "of the period":
				if (payDay?.value === "last day") {
					payDate = periodEnd;
				} else {
					let dayOfWeek = periodEnd.getDay(); // 0 (Sunday) - 6 (Saturday)
					let daysToSubtract = 0;
					switch (payDay?.value) {
						case "last Monday":
							daysToSubtract = (dayOfWeek + 6) % 7;
							break;
						case "last Tuesday":
							daysToSubtract = (dayOfWeek + 5) % 7;
							break;
						case "last Wednesday":
							daysToSubtract = (dayOfWeek + 4) % 7;
							break;
						case "last Thursday":
							daysToSubtract = (dayOfWeek + 3) % 7;
							break;
						case "last Friday":
							daysToSubtract = (dayOfWeek + 2) % 7;
							break;
					}
					payDate = new Date(
						periodEnd.getFullYear(),
						periodEnd.getMonth(),
						periodEnd.getDate() - daysToSubtract,
					);
				}
				break;
			case "of the month":
				payDate = set(periodEnd, { date: parseInt(payDay?.value) });
				break;
			case "days":
				payDate = addDays(periodEnd, parseInt(payDay?.value));
				break;
			case "workingDays":
				payDate = addBusinessDays(periodEnd, parseInt(payDay?.value));
				break;
			default:
				payDate = periodEnd;
				break;
		}
		return [paid, payDate];
	}, [JSON.stringify(payDay)]);

	const [periodDeadline, deadlineDate] = useMemo(() => {
		const deadlineDescription = {
			"of the month": "day of the month",
			"days": "day/s before payday",
		};

		const formatter = deadline?.type === "of the month" ? getNumberWithOrdinal : (value) => value;

		const prefix = ["of the month"].includes(deadline?.type) ? "on the " : "";

		let periodDeadline = `${prefix}${formatter(deadline?.value)} ${deadlineDescription[deadline?.type]}`;

		let deadlineDate;

		switch (deadline?.type) {
			case "of the month":
				deadlineDate = set(payDate, { date: parseInt(deadline?.value) });
				break;
			case "days":
				deadlineDate = subDays(payDate, parseInt(deadline?.value));
				break;
			default:
				deadlineDate = payDate;
				break;
		}
		return [periodDeadline, deadlineDate];
	}, [JSON.stringify(deadline)]);

	const exampleExecutionDates = ({ days, referenceDate, condition }) => {
		let date;

		switch (referenceDate) {
			case "payDay":
				referenceDate = payDate;
				break;
			case "deadline":
				referenceDate = deadlineDate;
				break;
			case "periodEnd":
			default:
				referenceDate = periodEnd;
				break;
		}

		switch (condition) {
			case "before":
				date = subDays(referenceDate, days);
				break;
			case "after":
				date = addDays(referenceDate, days);
				break;
			default:
				date = referenceDate;
				break;
		}

		return date;
	};

	return (
		<div className="form-group">
			<h5 style={{ paddingLeft: "1.25rem" }}>
				{name}
				<Tooltip
					as="span"
					content={
						<>
							<h6>{name}</h6>
							<p className="text-white text-left">
								Runs {payFrequency} times per year on a {payType} basis.
								<br />
								Employees are paid {paid}.
								<br />
								Period is due {periodDeadline}.
							</p>
						</>
					}
				>
					<InformationCircleIcon
						className="h-5 w-5"
						aria-hidden="true"
					/>
				</Tooltip>
			</h5>
			<div
				className="form-inner-section"
				style={{ marginBottom: "2rem" }}
			>
				<div className="col-xs-6">
					<div
						className="col-xs-12"
						style={{ minHeight: 63.57 }}
					>
						<div className="col-xs-4 pl-0 pr-0">
							<label>Pay Frequency</label>
							<div>
								{toTitleCase(payType)} ({payFrequency}/year)
							</div>
						</div>
						<div className="col-xs-4 pl-0 pr-0">
							<label>Pay Day</label>
							<div>{paid}</div>
						</div>
						<div className="col-xs-4 pl-0 pr-0">
							<label>Deadline</label>
							<div>{periodDeadline}</div>
						</div>
					</div>
					<div
						className="col-xs-12"
						style={{
							marginTop: "0.5rem",
							paddingTop: "0.5rem",
							border: "1px solid #e5e5e5",
							borderRadius: "10px",
						}}
					>
						<label>Pay Period Example</label>
						<div className="col-xs-4 pl-0">
							<div className="text-left">
								<div
									className="chip"
									style={{ background: "#ffdecd", color: "#ea611c", marginLeft: 0 }}
								>
									{format(periodStart, "do MMM")} - {format(periodEnd, "do MMM")}
								</div>
								<div className="help-block">
									Example {name} Payroll for {refDate.getFullYear()}
								</div>
							</div>
						</div>
						<div className="col-xs-4 pl-0">
							<div
								className="chip"
								style={{ background: "#ffdecd", color: "#ea611c", marginLeft: 0 }}
							>
								{format(payDate, "do MMM")}
							</div>
							<div className="help-block">Date Employees are Paid</div>
						</div>
						<div className="col-xs-4 pl-0">
							<div
								className="chip"
								style={{ background: "#ffdecd", color: "#ea611c", marginLeft: 0 }}
							>
								{format(deadlineDate, "do MMM")}
							</div>
							<div className="help-block">Payroll Deadline</div>
						</div>
					</div>
					<div className="clearfix" />
				</div>
				<div className="col-xs-6">
					{!everRollRule ? (
						<div style={{ display: "flex", justifyContent: "space-around", marginTop: "2rem" }}>
							<div style={{ textAlign: "center" }}>
								<BoltIcon style={{ height: "4rem", width: "4rem", color: "#ea611c" }} />
								<h4>EverRoll™ is not enabled</h4>
								<button
									className="btn btn-primary"
									style={{ margin: "1rem" }}
									onClick={() => openEverRollModal({ org, schedule: schedule?.id })}
								>
									<BoltIcon style={{ height: "1.5rem", width: "1.5rem", verticalAlign: "sub" }} />
									Enable EverRoll™
								</button>
							</div>
						</div>
					) : null}
					{everRollRule ? (
						<>
							<div
								className="col-xs-12"
								style={{ minHeight: 63.57 }}
							>
								<div
									className="col-xs-6 pl-0"
									style={{ paddingTop: "1rem" }}
								>
									<label>EverRoll™ is enabled</label>
									<div>
										{Object.values(everRollRule?.actions || {}).length} action/s have been set up
									</div>
								</div>
								<div
									className="col-xs-6 pl-0"
									style={{ paddingTop: "1rem" }}
								>
									<button
										className="btn btn-default pull-right"
										onClick={() => openEverRollModal({ org, schedule: schedule?.id })}
									>
										<BoltIcon style={{ height: "1.5rem", width: "1.5rem", verticalAlign: "sub" }} />
										EverRoll™ Settings
									</button>
								</div>
							</div>
							<div
								className="col-xs-12"
								style={{
									marginTop: "0.5rem",
									paddingTop: "0.5rem",
									border: "1px solid #e5e5e5",
									borderRadius: "10px",
								}}
							>
								<label>Example Execution Dates</label>
								{first3Rules?.map(([action, settings]) => {
									action = toTitleCase(action.replace("PAYPERIOD_", "").split("_").join(" "));
									const date = exampleExecutionDates(settings);
									return (
										<div className="col-xs-4 pl-0">
											<div className="text-left">
												<div
													className="chip"
													style={{
														background: "#ffdecd",
														color: "#ea611c",
														marginLeft: 0,
													}}
												>
													{format(date, "do MMM")}
												</div>
												<div className="help-block">
													{action} at {format(new Date(settings?.time), "Kaa")}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</>
					) : null}
				</div>
				<div className="clearfix" />
			</div>
			<div className="clearfix" />
		</div>
	);
}

const mapStateToProps = (state) => {
	const { orgPaySchedules, everRollRules } = state.advisors.everRollSettings || {};
	return {
		organisations: orgPaySchedules?.map((org) => ({
			value: org?.org,
			label: org?.name,
		})),
		everRollRules,
		orgPaySchedules,
		isAdmin: state.my.profile.isAdmin,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getEverRollSettings: (advisorId) => dispatch(ADVISOR_ACTIONS.getEverRollSettings(advisorId)),
	openEverRollModal: (status) => dispatch(ADVISOR_ACTIONS.openEverRollModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EverRoll);
